import { useMsal } from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getUniqueIdMap } from "../../common/generateUniqueId";
import SaveFormat from "../../common/save/SaveFormat";
import { selectFetchLogicsStatus } from "../logics/logicsSlice";
import { saveVersion } from "../menuManager/menu/menuImpl/ProjectMenu";
import css from "./Editor.module.css";
import { Header } from "./components/header/Header";
import { LogicsSelector } from "./components/logicsSelector/LogicsSelector";
import { Sheet } from "./components/sheet/Sheet";
import { SideBar } from "./components/sideBar/SideBar";
import { Tabs } from "./components/tabs/Tabs";
import {
	saveProjectOneDrive,
	selectCurrentTabIndex,
	selectEditor,
} from "./editorSlice";

function TabsAndSheets() {
	const currentTabIndex = useAppSelector(selectCurrentTabIndex);

	return (
		<div className={css.tabsAndSheets}>
			<Tabs />
			<Sheet sheetIndex={currentTabIndex} />
		</div>
	);
}

function DevArea() {
	return (
		<div className={css.devArea}>
			<SideBar />
			<TabsAndSheets />
		</div>
	);
}

export function Editor() {
	const dispatch = useAppDispatch();
	const logicsStatus = useAppSelector(selectFetchLogicsStatus);
	const editor = useAppSelector(selectEditor);
	const { instance } = useMsal();
	const { t } = useTranslation();
	const [debouncedEditor, setDebouncedEditor] = useState(editor);

	useEffect(() => {
		// Set a timeout to update debounced value after 5000ms
		const handler = setTimeout(() => {
			setDebouncedEditor(editor);
		}, 5000);

		// // Cleanup the timeout if `query` changes before 5000ms
		return () => {
			clearTimeout(handler);
		};
	}, [editor.devices, editor.sheets, editor.memories]);

	useEffect(() => {
		if (debouncedEditor) {
			if (logicsStatus.code === "succeeded" && editor.oneDrive) {
				const saveFormat: SaveFormat = {
					logicsCommitRef: logicsStatus.logics.commit,
					editor,
					saveVersion,
					idMap: getUniqueIdMap(),
				};

				dispatch(
					saveProjectOneDrive({
						saveFormat,
						instance,
						t,
						notify: false,
						itemId: editor.oneDrive.itemId,
						driveId: editor.oneDrive.driveId,
						update: true,
						newName: editor.oneDrive.name ?? null,
					}),
				);
			}
		}
	}, [debouncedEditor]);

	return (
		<div className={css.editor}>
			<Header />
			{logicsStatus.code !== "succeeded" && <LogicsSelector />}
			{logicsStatus.code === "succeeded" && <DevArea />}
		</div>
	);
}
